import type { Availability } from '@cocoonspace/sdk-js/types/availability'
import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { EventType } from '@cocoonspace/sdk-js/types/eventType'
import type { SimulateEntry } from '@cocoonspace/sdk-js/types/simulateEntry'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useMemo } from 'react'
import { useGetSpaceAvailability } from '@cocoonspace/sdk-js/domains/availabilities/availabilities'
import { getGetBookingQueryKey } from '@cocoonspace/sdk-js/domains/bookings/bookings'
import {
	useSimulateEvent,
	useUpdateEvent,
} from '@cocoonspace/sdk-js/domains/events/events'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Button } from '@cocoonspace/ui/components/button'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { usePathname, useRouter, useSearchParams } from '~/hooks/use-localized-navigation.hook'
import { toast } from 'sonner'

import { useCreateBookingOptions } from '~/domains/booking/hooks/use-create-booking-options.hook'
// import { useExperiment } from '~/hooks/use-experiment'
import { useIsRouterLoading } from '~/hooks/use-is-router-loading.hook'
import { useLocale } from '~/hooks/use-locale.hook'
import { getLocalizedPath } from '~/i18n/routing'
import { useHttpErrorMsg } from '~/lib/get-http-error-msg'
import { useAuth } from '~/providers/auth-provider'
import { AddToCartButton } from '../../../../../cart/add-to-cart-button'
import { GuestFooter } from '../guest-footer'
import { StepperHeader } from '../stepper-header'
import { BookingWeekdayPicker } from './booking-weekday-picker'
import { FooterDateTime } from './footer-date-time'
import { FooterPriceCreate } from './footer-price-create'
import { FooterPriceUpdate } from './footer-price-update'
import { MinBilledMinutes } from './min-billed-minutes'
import { TimeScheduler } from './time-scheduler'
import type { BookingOptions } from '../use-booking-options.hook'

const DEFAULT_START_TIME = '08:00'
const DEFAULT_END_TIME = '09:00'

export interface BookingOption {
	day: string
	start: string
	end: string
}

const BookButton = ({
	isRouterLoading,
	isPending,
	onSubmit,
	mode,
	type,
}: {
	isRouterLoading: boolean
	isPending: boolean
	onSubmit: () => void
	mode: 'create' | 'update'
	type: EventType
}) => {
	const { t } = useLingui()

	return (
		<Button
			className='rounded-full'
			data-testid='book-space-btn'
			disabled={isRouterLoading || isPending}
			onClick={onSubmit}
			size='sm'
			variant='gradient'
		>
			{mode === 'create' && type === 'meeting'
				? t`Réserver`
				: t`Choisir ce créneau`}
		</Button>
	)
}

const StepTimeFooter = ({
	type,
	value,
	mode,
	event,
	space,
	isAvailable,
	bookingOptions,
	availability,
	bookingSimulation,
	onChange,
	onSuccess,
}: {
	value: BookingOptions
	type: EventType
	mode: 'create' | 'update'
	event?: Event
	space: Space
	isAvailable: boolean
	availability?: Availability
	bookingSimulation?: SimulateEntry
	bookingOptions: BookingOption
	onChange: (value: BookingOptions) => void
	onSuccess?: () => void
}) => {
	const queryClient = useQueryClient()
	const router = useRouter()
	const pathname = usePathname() ?? ''
	const searchParams = useSearchParams()
	const locale = useLocale()
	// const hasCartXp = useExperiment('carts-beta')
	const httpErrorMsg = useHttpErrorMsg()
	const { t } = useLingui()
	const { isAuth, openRegister, setAuthErrorMsg } = useAuth()

	const eventUpdate = useUpdateEvent()
	const { mutateAsync: createBooking, isPending } = useCreateBookingOptions()

	const isRouterLoading = useIsRouterLoading()

	const timeslotDuration = useMemo(() => {
		const start = bookingOptions.start
		const end = bookingOptions.end

		if (!start || !end) return 0

		const [startHour, startMinute] = start.split(':').map(Number)
		const [endHour, endMinute] = end.split(':').map(Number)

		const startDate = new Date(0, 0, 0, startHour, startMinute)
		const endDate = new Date(0, 0, 0, endHour, endMinute)

		const diff = (endDate.getTime() - startDate.getTime()) / (1000 * 60) // duration in minutes

		return diff > 0 ? diff : 0
	}, [bookingOptions.start, bookingOptions.end])

	const onSubmit = () => {
		if (space.status !== 'published') return

		if (!bookingOptions.day || !bookingOptions.start || !bookingOptions.end) {
			return
		}

		if (!isAuth) {
			const params = new URLSearchParams(searchParams.toString())
			params.set('day', bookingOptions.day)
			params.set('start', bookingOptions.start)
			params.set('end', bookingOptions.end)
			router.push(`${pathname}?${params.toString()}`)

			openRegister()
			setAuthErrorMsg(t`Vous devez être connecté(e) pour réserver un Cocoon`)
			return
		}

		if (mode === 'create') {
			return createBooking({
				bookingOptions: [bookingOptions],
				space,
			})
				.then(({ id }) => {
					onSuccess?.()

					const params = new URLSearchParams(searchParams.toString())
					router.push(
						getLocalizedPath(
							`/booking/${id}/payment?${params.toString()}`,
							locale,
						),
					)
				})
				.catch((error) => {
					const errorCode = error?.response?.data?.errors?.[0].code as
						| string
						| undefined

					toast.error(
						httpErrorMsg[`api.${errorCode}`]
							? httpErrorMsg[`api.${errorCode}`]()
							: t`Une erreur est survenue`,
					)
				})
		}

		if (mode === 'update' && event) {
			return eventUpdate
				.mutateAsync({
					data: bookingOptions,
					eventId: event.id,
				})
				.then(() => {
					const queryKey = getGetBookingQueryKey(event.booking_id as string)
					queryClient.invalidateQueries({ queryKey })
					onSuccess?.()
				})
		}
	}

	const billedMinutes = availability?.billed_minutes || 0

	return (
		<>
			<hr className='border-slate-200' />
			<div className='flex min-h-[93px] flex-col gap-y-2 px-4 py-3'>
				<FooterDateTime
					value={bookingOptions}
					onChange={({ start, end }) => {
						return onChange({ ...value, end, start })
					}}
				/>

				{isAvailable && (
					<div className='flex items-center justify-between gap-x-2'>
						{mode === 'create' && type === 'meeting' && availability && (
							<FooterPriceCreate availability={availability} />
						)}

						{mode === 'update' && bookingSimulation && event && (
							<FooterPriceUpdate
								bookingSimulation={bookingSimulation}
								event={event}
							/>
						)}

						<div className='flex flex-col items-center gap-y-1'>
							{mode === 'create' && type === 'meeting' /* && hasCartXp */ ? (
								<AddToCartButton
									space={space}
									bookingOptions={[bookingOptions]}
								/>
							) : (
								<BookButton
									isPending={isPending}
									isRouterLoading={isRouterLoading}
									mode={mode}
									onSubmit={onSubmit}
									type={type}
								/>
							)}
						</div>
					</div>
				)}

				{!isAvailable && (
					<div className='min-h-[45px] text-sm'>
						{t`❗ Modifiez ou déplacez votre créneau sur une plage horaire disponible.`}
					</div>
				)}
			</div>

			{timeslotDuration < billedMinutes && (
				<MinBilledMinutes billedMinutes={billedMinutes} />
			)}
		</>
	)
}

export const StepTime = ({
	mode = 'create',
	value,
	event,
	space,
	maxDate,
	className,
	type,
	onChange,
	onSuccess,
	onClickBack,
}: {
	mode?: 'update' | 'create'
	type: EventType
	value: BookingOptions
	event?: Event
	maxDate: string
	space: Space
	className?: string
	onChange: (value: BookingOptions) => void
	onSuccess?: () => void
	onClickBack: () => void
}) => {
	const { isAuth } = useAuth()

	const bookingOptions = useMemo<BookingOption>(
		() => ({
			day: value.day?.[0] as string,
			end: value.end || DEFAULT_END_TIME,
			start: value.start || DEFAULT_START_TIME,
		}),
		[value],
	)

	const availabilityQuery = useGetSpaceAvailability(space.id, {
		...bookingOptions,
		type: type as any,
	})

	const bookingSimulationQuery = useSimulateEvent(event?.id as string, [
		bookingOptions,
	])

	const availability = availabilityQuery.data?.data
	const bookingSimulation = bookingSimulationQuery.data?.data[0]
	const notAvailableMsg = bookingSimulation?.error

	const isAvailable = useMemo(() => {
		if (mode === 'update') {
			return bookingSimulationQuery.isLoading ? true : !notAvailableMsg
		}

		return availabilityQuery.isLoading ? true : !!availability?.available
	}, [
		mode,
		bookingSimulationQuery.isLoading,
		notAvailableMsg,
		availabilityQuery.isLoading,
		availability,
	])

	return (
		<div
			className={className}
			data-testid='step-time'
		>
			<StepperHeader
				className='capitalize'
				onClickBack={onClickBack}
			>
				{dateTimeUtils(bookingOptions.day).format('dddd Do MMMM')}
			</StepperHeader>

			<BookingWeekdayPicker
				className='mb-2'
				max={maxDate}
				min={dateTimeUtils().customFormat('dateTech')}
				onChange={(day) => onChange({ ...bookingOptions, day: [day] })}
				value={bookingOptions.day}
			/>

			<div className='h-[380px] overflow-x-hidden overflow-y-scroll p-4'>
				<TimeScheduler
					eventId={event?.id}
					isAvailable={isAvailable}
					notAvailableMsg={notAvailableMsg}
					onTimeChange={(start, end) => onChange({ ...value, end, start })}
					spaceId={space.id}
					value={bookingOptions}
				/>
			</div>

			{!isAuth && <GuestFooter />}

			{isAuth && (
				<StepTimeFooter
					type={type}
					mode={mode}
					value={value}
					event={event}
					space={space}
					isAvailable={isAvailable}
					bookingOptions={bookingOptions}
					availability={availability}
					bookingSimulation={bookingSimulation}
					onChange={onChange}
					onSuccess={onSuccess}
				/>
			)}
		</div>
	)
}
